
<style lang="less">
.build-info {
  font-size: 0.8em;
  text-transform: uppercase;
  font-weight: bold;
  text-align: center;
  opacity: 0.5;
  margin:40px 0;
}
</style>

<template>
<screen :settings="{ screenIndex: props.screenIndex }" :style="{ opacity: isLoaded ? 1 : 0.001 }">
  <screen-header v-if="overridingNowTime === null" :title="title" :subtitle="subtitle" />
  <graph-details v-else :time="overridingNowTime" :day="d" />
  <day-graph v-if="d?.hours?.length" :date-number="dateNumber" @onNowTime="t => overridingNowTime = t" @load="onGraphLoad" />
  <day-post-graph :date-number="dateNumber" />
  <div style="height: 2px;"></div>
  <day-stat-rows :cells="cells" />
  <div class="build-info">BUILD: {{ buildTimestamp }}</div>
  <div style="height: 20px;"></div>
  <div style="height: 200px;" v-if="!d?.hours?.length"></div>
</screen>
</template>

<script lang="ts" setup>
import Screen from 'src/screen/screen.vue';
import ScreenHeader from 'src/screen/screen-header.vue';
import DayPostGraph from 'src/screen/day-screen/day-post-graph.vue';
import DayGraph from 'src/screen/day-screen/day-graph/day-graph.vue';
import DayStatRows from 'src/screen/day-screen/day-stat-rows.vue';
import { IStatCell } from 'src/screen/day-screen/day-stat-row.vue';
import dayjs from 'dayjs';
import { getComparativeSummary } from 'src/report/utils/comparative-summary';
import { clientContext } from 'src/client/context';
import _ from 'lodash';
import { clientFormatters } from 'src/utils/formatters.client';
import { ref } from 'vue';
import GraphDetails from 'src/screen/day-screen/graph-details.vue';
import { nativeService } from '@/native/service';

const props = defineProps<{
  screenIndex: number,
  dateNumber: IDateNumber,
}>();
const overridingNowTime = ref<number>(null);

const buildTimestamp = __BUILD_TIMESTAMP__;

const title = dayjs(Date.fromDateNum(props.dateNumber)).format('dddd, MMMM Do');
const subtitle = getComparativeSummary({ report: clientContext.report, dayNumber: props.dateNumber }); // `9° cooler than yesterday. 80% chance of rain.`;
const d = [...clientContext.report.days, clientContext.report.calendarDay].find(d => d?.dateNumber === props.dateNumber);

const cells:(IStatCell | string)[] = [
  'Wind',
  { label: 'Speed', value: clientFormatters.windSpeed(d.windSpeed), valueSuffix: clientFormatters.windUnits(), dull: !clientFormatters.windSpeedIsSignificant(d.windSpeed) },
  { label: 'Gust', value: clientFormatters.windSpeed(d.windMaxSpeed), valueSuffix: clientFormatters.windUnits(), dull: !clientFormatters.windSpeedIsSignificant(d.windMaxSpeed) },
  { label: 'Direction', value: clientFormatters.windDirection(d.windDirection), dull: !clientFormatters.windSpeedIsSignificant(d.windMaxSpeed) },
  { label: 'Clouds', value: Math.round(d.cloudCover * 100).toString(), valueSuffix: '%', dull: !clientFormatters.cloudCoverIsSignificant(d.cloudCover) },
  'Solar',
  { label: 'UV Index', value: d.maxUvIndex?.toFixed(1), dull: !clientFormatters.maxUvIndexIsSignificant(d.maxUvIndex) },
  { label: 'Moon', value: clientFormatters.moonPhase(d.moonPhase) },
  { label: 'Sunrise', value: clientFormatters.timeString(d.sunriseTime).replace(/[a-z]+/ig, ''), valueSuffix: clientFormatters.timeString(d.sunriseTime).replace(/[^a-z]/ig, '') },
  { label: 'Sunset', value: clientFormatters.timeString(d.sunsetTime).replace(/[a-z]+/gi, ''), valueSuffix: clientFormatters.timeString(d.sunsetTime).replace(/[^a-z]/ig, '') },
  'Allergies',
  ...(d.pollens || []).map(p => ({ label: p.title, value: `${(p.level || 0) * 20}`, valueSuffix: '%', dull: !p.level || p.level < 3 })),
  'Atmosphere',
  { label: 'Humidity', value: `${Math.round(d.humidity * 100)}`, valueSuffix: '%', dull: !clientFormatters.humidityIsSignificant(d.humidity) },
  { label: 'Dew Point', value: clientFormatters.temp(d.tempDewPoint), dull: !clientFormatters.dewPointIsSignificant(d.tempDewPoint) },
  { label: 'Updated', value: clientFormatters.date(clientContext.report.current.time, 'h:mm'), valueSuffix: clientFormatters.date(clientContext.report.current.time, 'a'), dull: true },
].filter(x => typeof x === 'string' || (x.label && x.value));

const showConvectiveOutlook = () => {
  nativeService.run('open-url', { url: `https://www.spc.noaa.gov/products/outlook/day${d.daysFromToday + 1}otlk_prt.html` });
};
if ([0, 1, 2].includes(d.daysFromToday)) cells.push({ label: 'OUTLOOK', value: 'NOAA', click: showConvectiveOutlook });

const isLoaded = ref(false);
const onGraphLoad = () => {
  isLoaded.value = true;
};

</script>
